import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BellOff } from 'lucide-react';
import { useAuth } from '@/lib/auth';

// Routes where this re-engagement banner must never appear. Mirrors the same
// list used by IOSInstallBanner / DesktopSidebar: full-screen takeovers and
// auth flows must not be interrupted by app chrome.
const SUPPRESSED_PATHS = [
  '/login',
  '/register',
  '/verify-email',
  '/onboarding',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/install-prompt',
  '/push-prompt',
  '/vip-offer',
  '/install-app',
  '/survey',
  '/subscription',
  '/delete-account',
  '/legal',
  '/privacy',
  '/terms',
];

const STORAGE_KEY = 'papacito_push_declined_banner_last_shown';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
// Re-engagement cooldown: per spec, max once per 30 days on all platforms.
const REENGAGE_MS = 30 * 24 * 60 * 60 * 1000;

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function localCooldownOk(): boolean {
  try {
    const last = localStorage.getItem(STORAGE_KEY);
    if (last && Date.now() - Number(last) < THIRTY_DAYS_MS) return false;
  } catch { /* ignore */ }
  return true;
}

export function PushDeclinedBanner() {
  const { token } = useAuth();
  const [location] = useLocation();
  const [visible, setVisible] = useState(false);

  const isSuppressedPath = SUPPRESSED_PATHS.some(p => location === p || location.startsWith(p + '/'));

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'denied') return;
    if (!localCooldownOk()) return;
    if (!token) return;
    if (isSuppressedPath) return;

    let cancelled = false;
    (async () => {
      try {
        // Check the authoritative server record of when the user last
        // declined permission. If they declined recently, defer per-platform
        // re-engagement window — do not nag them.
        const r = await fetch('/api/push/behavioral-context', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const ctx = await r.json();
        const lastDeclined = ctx?.lastDeclinedAt ? new Date(ctx.lastDeclinedAt).getTime() : 0;
        if (lastDeclined > 0 && Date.now() - lastDeclined < REENGAGE_MS) return;
        if (cancelled) return;
        setTimeout(() => { if (!cancelled) setVisible(true); }, 4000);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [token, isSuppressedPath]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          style={{
            position: 'fixed',
            top: 'calc(8px + env(safe-area-inset-top))',
            left: 12,
            right: 12,
            zIndex: 9000,
            background: 'rgba(20,8,12,0.96)',
            border: '1px solid rgba(225,29,72,0.4)',
            borderRadius: 14,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            maxWidth: 540,
            margin: '0 auto',
          }}
          data-testid="push-declined-banner"
        >
          <BellOff size={18} color="#e11d48" />
          <div style={{ flex: 1, color: 'rgba(255,255,255,0.9)', fontSize: 13, lineHeight: 1.4 }}>
            <strong style={{ color: '#fff' }}>Notifications are off.</strong>{' '}
            {isIOS()
              ? 'Open iPhone Settings → Notifications → Papacito to turn them back on.'
              : 'Open your browser site settings → Notifications → Allow to turn them back on.'}
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <X size={16} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
